import { createFileRoute } from '@tanstack/react-router'
import { ErrorBanner } from '@workspace/ui/components/error-banner'
import { LoadingSpinner } from '@workspace/ui/components/loading-spinner'
import { MetricCard } from '@workspace/ui/components/metric-card'
import { Tabs, TabsList, TabsTrigger } from '@workspace/ui/components/tabs'
import { ActivitySection } from '../../features/admin/components/dashboard/activity-section'
import { formatPercent } from '../../features/admin/components/dashboard/dashboard-format'
import { EngagementSection } from '../../features/admin/components/dashboard/engagement-section'
import { EvaluationsSection } from '../../features/admin/components/dashboard/evaluations-section'
import { GrowthSection } from '../../features/admin/components/dashboard/growth-section'
import { OnboardingSection } from '../../features/admin/components/dashboard/onboarding-section'
import { OutreachSection } from '../../features/admin/components/dashboard/outreach-section'
import { RevenueSection } from '../../features/admin/components/dashboard/revenue-section'
import { useAdminDashboardQuery } from '../../features/admin/hooks/use-admin-api'

type SearchParams = {
  days?: number | 'all'
}

const RANGES: Array<number | 'all'> = [7, 30, 90, 'all']

export const Route = createFileRoute('/admin/')({
  component: AdminDashboardPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    days:
      search.days === 'all'
        ? 'all'
        : RANGES.includes(Number(search.days))
          ? Number(search.days)
          : undefined,
  }),
})

function AdminDashboardPage() {
  const { days = 30 } = Route.useSearch()
  const navigate = Route.useNavigate()

  const dashboardQuery = useAdminDashboardQuery(days)
  const data = dashboardQuery.data?.data

  const isAllTime = days === 'all'
  const rangeLabel = isAllTime ? 'All time' : `Last ${days} days`

  return (
    <div className="w-full space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-lg font-semibold">Dashboard</h1>
        <Tabs
          value={String(days)}
          onValueChange={(value) =>
            navigate({ search: { days: value === 'all' ? 'all' : Number(value) } })
          }
        >
          <TabsList>
            {RANGES.map((range) => (
              <TabsTrigger key={range} value={String(range)}>
                {range === 'all' ? 'All time' : `${range}d`}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {dashboardQuery.isLoading && (
        <div className="flex justify-center py-24">
          <LoadingSpinner size="lg" />
        </div>
      )}
      {dashboardQuery.isError && <ErrorBanner message="Failed to load dashboard metrics." />}

      {data && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            <MetricCard
              label="Total users"
              value={(isAllTime ? data.totals.users : data.range.newUsers).toLocaleString()}
              subtitle={rangeLabel}
              variant="highlight"
            />
            <MetricCard
              label="Founders"
              value={(isAllTime ? data.totals.founders : data.range.newFounders).toLocaleString()}
              subtitle={rangeLabel}
            />
            <MetricCard
              label="Investor users"
              value={(isAllTime
                ? data.totals.investorUsers
                : data.range.newInvestorUsers
              ).toLocaleString()}
              subtitle={rangeLabel}
            />
            <MetricCard
              label="Startups"
              value={(isAllTime ? data.totals.startups : data.range.newStartups).toLocaleString()}
              subtitle={rangeLabel}
            />
            <MetricCard
              label="Investor orgs"
              value={(isAllTime
                ? data.totals.investorOrgs
                : data.range.newInvestorOrgs
              ).toLocaleString()}
              subtitle={rangeLabel}
            />
            <MetricCard
              label="Onboarding completion"
              value={formatPercent(data.range.onboardingCompletionRate)}
              subtitle="Of new signups"
              variant={data.range.onboardingCompletionRate < 0.5 ? 'warning' : 'success'}
            />
          </div>

          <ActivitySection data={data} />
          <GrowthSection data={data} />
          <OnboardingSection data={data} />
          <EvaluationsSection data={data} />
          <OutreachSection data={data} />
          <EngagementSection data={data} />
          <RevenueSection data={data} />
        </>
      )}
    </div>
  )
}
